import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../assets/style.css";
import "../assets/services.css";
import "../styles/softwarePublic.css";

interface SoftwareItem {
  id: number;
  title: string;
  description: string;
  image: string | null;
}

interface SoftwareModule {
  id: number;
  software_id: number;
  software_title: string;
  title: string;
  description: string;
  image: string | null;
  link: string;
  status: string;
}

const BASE_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api";

const IMAGE_URL =
  import.meta.env.VITE_LYRAZONE_IMAGE_URL || "http://localhost:5000/uploads";

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const SoftwareModules: React.FC = () => {
  const { softwareSlug = "" } = useParams();
  const navigate = useNavigate();

  const cleanSoftwareSlug = softwareSlug.replace(/-modules$/, "");

  const [softwareList, setSoftwareList] = useState<SoftwareItem[]>([]);
  const [modules, setModules] = useState<SoftwareModule[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const selectedSoftware = useMemo(() => {
    return softwareList.find(
      (item) => slugify(item.title) === cleanSoftwareSlug,
    );
  }, [softwareList, cleanSoftwareSlug]);

  const filteredModules = useMemo(() => {
    let list = modules;

    if (selectedSoftware) {
      list = list.filter(
        (item) => Number(item.software_id) === Number(selectedSoftware.id),
      );
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (item) =>
          item.title?.toLowerCase().includes(q) ||
          item.description?.toLowerCase().includes(q),
      );
    }

    return list;
  }, [modules, selectedSoftware, search]);

  const getModuleImage = (image: string | null) => {
    if (!image) return "/src/assets/img/saas/elbs-erp.jpg";
    if (image.startsWith("http")) return image;
    return `${IMAGE_URL}/uploads/software-modules/${image}`;
  };

  const openModuleDetail = (link: string) => {
    if (!link) return;

    const cleanLink = link.startsWith("/") ? link : `/${link}`;
    navigate(cleanLink);
  };

  const fetchData = async () => {
    try {
      const [softwareRes, modulesRes] = await Promise.all([
        fetch(`${BASE_URL}/admin/software/website`),
        fetch(`${BASE_URL}/admin/software/modules/website`),
      ]);

      const softwareResult = await softwareRes.json();
      const modulesResult = await modulesRes.json();

      if (softwareResult.success) {
        setSoftwareList(softwareResult.data || []);
      }

      if (modulesResult.success) {
        setModules(modulesResult.data || []);
      }
    } catch (error) {
      console.error("Fetch Software Modules Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      <div id="home-p" className="home-p pages-head6 text-center">
        <div className="container"></div>
      </div>

      <section className="software-public-page container-fluid">
        <div className="row">
          <aside className="col-lg-3 col-md-4">
            <div className="software-filter-box">
              <h4>SEARCH FILTER</h4>

              <div className="software-filter-divider"></div>

              <label>SEARCH MODULE</label>
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <div className="software-filter-divider"></div>

              <h5>MODULES</h5>
              <p className="software-filter-count">
                {filteredModules.length} module(s) found
              </p>
            </div>
          </aside>

          <div className="col-lg-9 col-md-8">
            <div className="software-public-heading">
              <span>Lyra Zone Solutions</span>
              <h1>{selectedSoftware?.title || "Software Modules"}</h1>
              <p>
                Explore available modules and choose the right solution for your
                business workflow.
              </p>
            </div>

            {loading ? (
              <p>Loading modules...</p>
            ) : filteredModules.length > 0 ? (
              <div className="row">
                {filteredModules.map((item) => (
                  <div key={item.id} className="col-lg-4 col-md-6 mb-4">
                    <div
                      className="software-module-card"
                      onClick={() => openModuleDetail(item.link)}
                    >
                      <div className="software-module-img-wrap">
                        <img
                          src={getModuleImage(item.image)}
                          alt={item.title}
                          className="module-preview-image"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewImage(getModuleImage(item.image));
                          }}
                        />
                      </div>

                      <div className="software-module-content">
                        <h3>{item.title}</h3>
                        <p>{item.description}</p>

                        <button type="button">
                          View Details <i className="fa fa-arrow-right"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="software-empty-box">
                No modules available at the moment.
              </div>
            )}
          </div>
        </div>
      </section>
      {previewImage && (
        <div className="solution-image-fullscreen">
          <button
            type="button"
            className="solution-image-close-btn"
            onClick={() => setPreviewImage(null)}
          >
            ✕
          </button>

          <img
            src={previewImage}
            alt="Preview"
            className="solution-image-fullscreen-img"
          />
        </div>
      )}
    </>
  );
};

export default SoftwareModules;
